import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature';
import { collectFeatureFaceWorldVertices } from './measureMeshFaceClosestPoints';
import {
    MeasureDistancePrimitiveKind,
    type MeasureDistanceGeometry,
} from '../types/measure-distance-types';

/** 判定退化距离的容差（世界单位）。 */
const MEASURE_FARTHEST_DISTANCE_EPSILON = 1e-9;

/**
 * 两求距几何体之间的最远点对。
 */
export interface MeasureFarthestPointPair {
    /** 几何体 A 上的最远点。 */
    pointA: THREE.Vector3;
    /** 几何体 B 上的最远点。 */
    pointB: THREE.Vector3;
}

/**
 * 求解两几何体之间的最远点对。
 *
 * 设计说明：
 * 1. 无限直线/无限平面参与时最远距无界，返回 null；
 * 2. 有边界几何体在代表点集（端点/折线顶点/面网格顶点）上取最大点对。
 *
 * @param geometryA 几何体 A。
 * @param geometryB 几何体 B。
 * @returns 最远点对；无法求解时返回 null。
 */
export function solveFarthestPointsBetweenGeometries(
    geometryA: MeasureDistanceGeometry,
    geometryB: MeasureDistanceGeometry,
): MeasureFarthestPointPair | null {
    if (isUnboundedDistanceGeometry(geometryA) || isUnboundedDistanceGeometry(geometryB)) {
        return null;
    }

    const rankA = measureDistancePrimitiveRank(geometryA.kind);
    const rankB = measureDistancePrimitiveRank(geometryB.kind);
    if (rankA <= rankB) {
        return dispatchFarthestPoints(geometryA, geometryB);
    }

    const reversed = dispatchFarthestPoints(geometryB, geometryA);
    if (!reversed) {
        return null;
    }

    return {
        pointA: reversed.pointB,
        pointB: reversed.pointA,
    };
}

/**
 * 判断求距基元是否含无界延伸（最远距不存在）。
 *
 * @param geometry 求距几何体。
 * @returns 是否为无界几何体。
 */
function isUnboundedDistanceGeometry(geometry: MeasureDistanceGeometry): boolean {
    return geometry.kind === MeasureDistancePrimitiveKind.InfiniteLine
        || geometry.kind === MeasureDistancePrimitiveKind.Plane;
}

/**
 * 基元优先级（数值越小越优先作为 dispatch 左侧）。
 *
 * @param kind 基元种类。
 * @returns 优先级。
 */
function measureDistancePrimitiveRank(kind: MeasureDistancePrimitiveKind): number {
    switch (kind) {
        case MeasureDistancePrimitiveKind.Point:
            return 0;
        case MeasureDistancePrimitiveKind.Segment:
            return 1;
        case MeasureDistancePrimitiveKind.Polyline:
            return 2;
        case MeasureDistancePrimitiveKind.MeshFace:
            return 3;
        default:
            return 99;
    }
}

/**
 * 按左侧基元种类分派最远点求解。
 *
 * @param geometryA 几何体 A。
 * @param geometryB 几何体 B。
 * @returns 最远点对（点 A 在 geometryA 上，点 B 在 geometryB 上）。
 */
function dispatchFarthestPoints(
    geometryA: MeasureDistanceGeometry,
    geometryB: MeasureDistanceGeometry,
): MeasureFarthestPointPair | null {
    switch (geometryA.kind) {
        case MeasureDistancePrimitiveKind.Point:
            return farthestPointToGeometry(geometryA.position, geometryB);
        case MeasureDistancePrimitiveKind.Segment:
            return farthestSegmentToGeometry(geometryA.start, geometryA.end, geometryB);
        case MeasureDistancePrimitiveKind.Polyline:
            return farthestPolylineToGeometry(geometryA.points, geometryB);
        case MeasureDistancePrimitiveKind.MeshFace:
            return farthestMeshFaceToGeometry(geometryA.face, geometryB);
        default:
            return null;
    }
}

/**
 * 求点到任意有边界几何体的最远点对。
 *
 * @param point 查询点。
 * @param geometry 目标几何体。
 * @returns 最远点对（查询点侧为 pointA）。
 */
function farthestPointToGeometry(
    point: THREE.Vector3,
    geometry: MeasureDistanceGeometry,
): MeasureFarthestPointPair | null {
    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Point:
            return { pointA: point.clone(), pointB: geometry.position.clone() };
        case MeasureDistancePrimitiveKind.Segment: {
            const onStart = geometry.start.distanceTo(point);
            const onEnd = geometry.end.distanceTo(point);
            if (onStart >= onEnd) {
                return { pointA: point.clone(), pointB: geometry.start.clone() };
            }
            return { pointA: point.clone(), pointB: geometry.end.clone() };
        }
        case MeasureDistancePrimitiveKind.Polyline: {
            return farthestPointToPointSet(point, geometry.points);
        }
        case MeasureDistancePrimitiveKind.MeshFace: {
            const vertices = collectFeatureFaceWorldVertices(geometry.face);
            return farthestPointToPointSet(point, vertices);
        }
        default:
            return null;
    }
}

/**
 * 求线段到有边界几何体的最远点对。
 *
 * @param start 线段起点。
 * @param end 线段终点。
 * @param geometry 目标几何体。
 * @returns 最远点对。
 */
function farthestSegmentToGeometry(
    start: THREE.Vector3,
    end: THREE.Vector3,
    geometry: MeasureDistanceGeometry,
): MeasureFarthestPointPair | null {
    switch (geometry.kind) {
        case MeasureDistancePrimitiveKind.Point: {
            const onStart = start.distanceTo(geometry.position);
            const onEnd = end.distanceTo(geometry.position);
            if (onStart >= onEnd) {
                return { pointA: start.clone(), pointB: geometry.position.clone() };
            }
            return { pointA: end.clone(), pointB: geometry.position.clone() };
        }
        case MeasureDistancePrimitiveKind.Segment:
            return farthestBetweenTwoSegments(start, end, geometry.start, geometry.end);
        case MeasureDistancePrimitiveKind.Polyline:
            return farthestSegmentToPointSet(start, end, collectPolylinePoints(geometry.points));
        case MeasureDistancePrimitiveKind.MeshFace:
            return farthestSegmentToPointSet(start, end, collectFeatureFaceWorldVertices(geometry.face));
        default:
            return null;
    }
}

/**
 * 求折线到有边界几何体的最远点对。
 *
 * @param points 折线点列。
 * @param geometry 目标几何体。
 * @returns 最远点对。
 */
function farthestPolylineToGeometry(
    points: readonly THREE.Vector3[],
    geometry: MeasureDistanceGeometry,
): MeasureFarthestPointPair | null {
    let best: MeasureFarthestPointPair | null = null;
    let bestDistanceSq = -1;

    for (let index = 1; index < points.length; index += 1) {
        const segmentResult = farthestSegmentToGeometry(
            points[index - 1]!,
            points[index]!,
            geometry,
        );
        if (!segmentResult) {
            continue;
        }
        const distanceSq = segmentResult.pointA.distanceToSquared(segmentResult.pointB);
        if (distanceSq > bestDistanceSq) {
            bestDistanceSq = distanceSq;
            best = segmentResult;
        }
    }

    return best;
}

/**
 * 求网格面到有边界几何体的最远点对。
 *
 * @param face 面代理。
 * @param geometry 目标几何体。
 * @returns 最远点对。
 */
function farthestMeshFaceToGeometry(
    face: FeatureFace,
    geometry: MeasureDistanceGeometry,
): MeasureFarthestPointPair | null {
    if (geometry.kind === MeasureDistancePrimitiveKind.MeshFace) {
        return farthestBetweenMeshFaces(face, geometry.face);
    }

    const vertices = collectFeatureFaceWorldVertices(face);
    if (geometry.kind === MeasureDistancePrimitiveKind.Point) {
        return farthestPointToPointSet(geometry.position, vertices, true);
    }
    if (geometry.kind === MeasureDistancePrimitiveKind.Segment) {
        return farthestSegmentToPointSet(geometry.start, geometry.end, vertices, true);
    }
    if (geometry.kind === MeasureDistancePrimitiveKind.Polyline) {
        return farthestBetweenPointSets(collectPolylinePoints(geometry.points), vertices);
    }

    return null;
}

/**
 * 求点到点集的最远点对。
 *
 * @param point 查询点。
 * @param targets 目标点集。
 * @param swapResult 是否交换结果（目标点集对应 geometryA 侧）。
 * @returns 最远点对。
 */
function farthestPointToPointSet(
    point: THREE.Vector3,
    targets: readonly THREE.Vector3[],
    swapResult: boolean = false,
): MeasureFarthestPointPair | null {
    if (targets.length <= 0) {
        return null;
    }

    let bestTarget = targets[0]!;
    let bestDistanceSq = point.distanceToSquared(bestTarget);

    for (let index = 1; index < targets.length; index += 1) {
        const candidate = targets[index]!;
        const distanceSq = point.distanceToSquared(candidate);
        if (distanceSq > bestDistanceSq) {
            bestDistanceSq = distanceSq;
            bestTarget = candidate;
        }
    }

    if (swapResult) {
        return { pointA: bestTarget.clone(), pointB: point.clone() };
    }
    return { pointA: point.clone(), pointB: bestTarget.clone() };
}

/**
 * 求线段到点集的最远点对。
 *
 * @param start 线段起点。
 * @param end 线段终点。
 * @param targets 目标点集。
 * @param swapResult 是否交换结果（线段对应 geometryB 侧）。
 * @returns 最远点对。
 */
function farthestSegmentToPointSet(
    start: THREE.Vector3,
    end: THREE.Vector3,
    targets: readonly THREE.Vector3[],
    swapResult: boolean = false,
): MeasureFarthestPointPair | null {
    let best: MeasureFarthestPointPair | null = null;
    let bestDistanceSq = -1;

    targets.forEach((target) => {
        const onStart = start.distanceToSquared(target);
        const onEnd = end.distanceToSquared(target);
        const useStart = onStart >= onEnd;
        const pointOnSegment = useStart ? start : end;
        const distanceSq = useStart ? onStart : onEnd;
        if (distanceSq <= bestDistanceSq) {
            return;
        }
        bestDistanceSq = distanceSq;
        best = swapResult
            ? { pointA: target.clone(), pointB: pointOnSegment.clone() }
            : { pointA: pointOnSegment.clone(), pointB: target.clone() };
    });

    return best;
}

/**
 * 求两线段之间的最远点对。
 *
 * 设计说明：三维线段间最远距必在四条端点配对之一取得。
 *
 * @param startA 线段 A 起点。
 * @param endA 线段 A 终点。
 * @param startB 线段 B 起点。
 * @param endB 线段 B 终点。
 * @returns 最远点对。
 */
function farthestBetweenTwoSegments(
    startA: THREE.Vector3,
    endA: THREE.Vector3,
    startB: THREE.Vector3,
    endB: THREE.Vector3,
): MeasureFarthestPointPair | null {
    const candidates: MeasureFarthestPointPair[] = [
        { pointA: startA.clone(), pointB: startB.clone() },
        { pointA: startA.clone(), pointB: endB.clone() },
        { pointA: endA.clone(), pointB: startB.clone() },
        { pointA: endA.clone(), pointB: endB.clone() },
    ];

    let best = candidates[0]!;
    let bestDistanceSq = best.pointA.distanceToSquared(best.pointB);
    for (let index = 1; index < candidates.length; index += 1) {
        const candidate = candidates[index]!;
        const distanceSq = candidate.pointA.distanceToSquared(candidate.pointB);
        if (distanceSq > bestDistanceSq) {
            bestDistanceSq = distanceSq;
            best = candidate;
        }
    }

    return best;
}

/**
 * 求两网格面之间的最远点对。
 *
 * @param faceA 面 A。
 * @param faceB 面 B。
 * @returns 最远点对。
 */
function farthestBetweenMeshFaces(
    faceA: FeatureFace,
    faceB: FeatureFace,
): MeasureFarthestPointPair | null {
    return farthestBetweenPointSets(
        collectFeatureFaceWorldVertices(faceA),
        collectFeatureFaceWorldVertices(faceB),
    );
}

/**
 * 求两组点之间的最远点对。
 *
 * @param pointsA 点集 A。
 * @param pointsB 点集 B。
 * @returns 最远点对。
 */
function farthestBetweenPointSets(
    pointsA: readonly THREE.Vector3[],
    pointsB: readonly THREE.Vector3[],
): MeasureFarthestPointPair | null {
    if (pointsA.length <= 0 || pointsB.length <= 0) {
        return null;
    }

    let best: MeasureFarthestPointPair | null = null;
    let bestDistanceSq = -1;

    pointsA.forEach((pointA) => {
        pointsB.forEach((pointB) => {
            const distanceSq = pointA.distanceToSquared(pointB);
            if (distanceSq <= bestDistanceSq) {
                return;
            }
            bestDistanceSq = distanceSq;
            best = { pointA: pointA.clone(), pointB: pointB.clone() };
        });
    });

    return best;
}

/**
 * 收集折线代表点（去重相邻重合点）。
 *
 * @param points 折线点列。
 * @returns 代表点列。
 */
function collectPolylinePoints(points: readonly THREE.Vector3[]): THREE.Vector3[] {
    if (points.length <= 0) {
        return [];
    }

    const result: THREE.Vector3[] = [points[0]!.clone()];
    for (let index = 1; index < points.length; index += 1) {
        const point = points[index]!;
        const previous = result[result.length - 1]!;
        if (previous.distanceToSquared(point) > MEASURE_FARTHEST_DISTANCE_EPSILON) {
            result.push(point.clone());
        }
    }
    return result;
}
